import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Sparkles } from "lucide-react";
import AITextGenerator from "@/components/assistant/AITextGenerator";

const CATEGORIES = [
  { value: "food_safety", label: "Seguretat alimentària" },
  { value: "equipment_failure", label: "Avaria d'equip" },
  { value: "temperature_deviation", label: "Desviació temperatura" },
  { value: "cleaning_issue", label: "Problema de neteja" },
  { value: "supplier_issue", label: "Problema proveïdor" },
  { value: "staff_issue", label: "Incidència personal" },
  { value: "other", label: "Altres" },
];

const SEVERITIES = [
  { value: "low", label: "Baixa" },
  { value: "medium", label: "Mitjana" },
  { value: "high", label: "Alta" },
  { value: "critical", label: "Crítica" },
];

export default function IncidentForm({ open, onClose, onSave, venueId, user, editData }) {
  const [form, setForm] = useState(editData || {
    title: "",
    category: "food_safety",
    description: "",
    severity: "medium",
    corrective_action: "",
    status: "open",
  });
  const [saving, setSaving] = useState(false);
  const [aiTarget, setAiTarget] = useState(null); // "description" | "corrective_action"

  React.useEffect(() => {
    if (editData) setForm(editData);
    else setForm({
      title: "",
      category: "food_safety",
      description: "",
      severity: "medium",
      corrective_action: "",
      status: "open",
    });
  }, [editData, open]);

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    await onSave({
      ...form,
      venue_id: venueId,
      responsible_email: user.email,
      responsible_name: user.full_name,
      date: form.date || new Date().toISOString().split("T")[0],
    });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editData ? "Editar Incidència" : "Nova Incidència"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label>Títol</Label>
            <Input
              placeholder="Descriu breument la incidència..."
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gravetat</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {editData && (
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Oberta</SelectItem>
                  <SelectItem value="in_progress">En procés</SelectItem>
                  <SelectItem value="closed">Tancada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Descripció</Label>
              <button
                type="button"
                onClick={() => setAiTarget("description")}
                className="flex items-center gap-1 text-[11px] font-semibold text-[#7B2D3F] hover:opacity-70 transition-opacity"
              >
                <Sparkles className="w-3 h-3" /> Generar amb IA
              </button>
            </div>
            <Textarea
              placeholder="Detalla la incidència..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="h-20 resize-none"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Acció correctiva</Label>
              <button
                type="button"
                onClick={() => setAiTarget("corrective_action")}
                className="flex items-center gap-1 text-[11px] font-semibold text-[#7B2D3F] hover:opacity-70 transition-opacity"
              >
                <Sparkles className="w-3 h-3" /> Generar amb IA
              </button>
            </div>
            <Textarea
              placeholder="Quina acció s'ha pres o s'ha de prendre?"
              value={form.corrective_action}
              onChange={(e) => setForm({ ...form, corrective_action: e.target.value })}
              className="h-16 resize-none"
            />
          </div>
        </div>
        <AITextGenerator
          open={!!aiTarget}
          onClose={() => setAiTarget(null)}
          onApply={(text) => setForm(f => ({ ...f, [aiTarget]: text }))}
          title={aiTarget === "corrective_action" ? "Generar acció correctiva" : "Generar descripció"}
          placeholder="El text generat apareixerà aquí..."
          prompt={
            aiTarget === "corrective_action"
              ? `Genera una acció correctiva professional per a una incidència de tipus "${form.category}" amb títol "${form.title || "incidència"}" i gravetat ${form.severity}.${form.description ? ` Descripció: ${form.description}` : ""}
Inclou: 1) Acció immediata, 2) Mesures preventives, 3) Termini recomanat.
Escriu en primera persona del plural (hem de..., s'ha de...). Sigues concret i pràctic.`
              : `Genera una descripció detallada per a una incidència de restauració de categoria "${form.category}" amb títol "${form.title || "incidència"}".
Inclou: situació trobada, possible causa i impacte potencial. Sigues formal i tècnic però comprensible.`
          }
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button
            onClick={handleSave}
            disabled={!form.title || saving}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editData ? "Actualitzar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}